import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import TrainingGuide from "../common/TrainingGuide";
import dashboardSteps from "../../data/training/dashboard";

async function get(path: string) {
  const r = await fetch(`/api${path}`, { credentials: "include" });
  if (!r.ok) throw new Error(`${r.status}`);
  return r.json();
}

function useGet<T>(path: string) {
  const [data, setData]       = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    setLoading(true); setError(null);
    get(path)
      .then(d  => alive && (setData(d), setLoading(false)))
      .catch(e => alive && (setError(e.message), setLoading(false)));
    return () => { alive = false; };
  }, [path]);
  return { data, loading, error };
}

function Skeleton({ w = "100%", h = 14 }: { w?: string | number; h?: number }) {
  return <div style={{ width: w, height: h, background: "rgba(255,255,255,0.05)", borderRadius: 5, animation: "pulse 1.6s ease-in-out infinite" }} />;
}

function KPIBox({ label, value, sub, color, loading }: { label: string; value: React.ReactNode; sub?: string; color: string; loading?: boolean }) {
  return (
    <div style={{ background: "rgba(255,255,255,0.02)", border: "0.5px solid rgba(189,178,255,0.15)", borderRadius: 16, padding: "18px 20px", backdropFilter: "blur(60px)", WebkitBackdropFilter: "blur(60px)", borderTop: `2px solid ${color}66`, boxShadow: "0 4px 24px rgba(189,178,255,0.04)" }}>
      {loading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}><Skeleton w="55%" h={11} /><Skeleton w="70%" h={26} /><Skeleton w="45%" h={10} /></div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <div style={{ fontSize: 10, color: "#a3abc3", textTransform: "uppercase", letterSpacing: "0.12em", fontFamily: "'Space Grotesk',sans-serif" }}>{label}</div>
          <div style={{ fontSize: 26, fontWeight: 800, color, fontFamily: "'Manrope', sans-serif", lineHeight: 1.1, letterSpacing: "-0.02em" }}>{value ?? "—"}</div>
          {sub && <div style={{ fontSize: 11, color: "#6d758b", fontFamily: "'Space Grotesk',sans-serif" }}>{sub}</div>}
        </div>
      )}
    </div>
  );
}

function Tag({ children, color }: { children: React.ReactNode; color?: string }) {
  const c = color || "#6b7280";
  return <span style={{ fontSize: 10, padding: "1px 7px", borderRadius: 99, fontWeight: 600, background: c + "18", color: c, letterSpacing: "0.04em" }}>{children}</span>;
}

function Row({ children }: { children: React.ReactNode }) {
  return <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", background: "rgba(255,255,255,0.025)", borderRadius: 8, fontSize: 12 }}>{children}</div>;
}

function Bar({ pct, color }: { pct: number; color: string }) {
  return (
    <div style={{ height: 5, background: "rgba(255,255,255,0.06)", borderRadius: 3, overflow: "hidden" }}>
      <div style={{ height: "100%", width: `${Math.min(100, Math.max(0, pct))}%`, background: color, borderRadius: 3, transition: "width 0.5s ease" }} />
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 10, color: "#6d758b", textTransform: "uppercase", letterSpacing: "0.14em", marginBottom: 10, fontFamily: "'Space Grotesk',sans-serif", fontWeight: 600 }}>{children}</div>;
}

function ModuleCard({ title, icon, href, accent = "#ffd6a5", loading, error, children }: {
  title: string; icon: string; href: string; accent?: string; loading?: boolean; error?: string | null; children: React.ReactNode;
}) {
  const nav = useNavigate();
  const [hov, setHov] = useState(false);
  return (
    <div onClick={() => nav(href)} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ background: hov ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.02)", border: `0.5px solid ${hov ? "rgba(189,178,255,0.35)" : "rgba(189,178,255,0.12)"}`, borderRadius: 20, padding: "22px 24px", cursor: "pointer", transition: `all var(--morph-speed,0.4s) ease`, backdropFilter: "blur(60px)", WebkitBackdropFilter: "blur(60px)", transform: hov ? "translateY(-2px)" : "none", boxShadow: hov ? `0 16px 48px rgba(189,178,255,0.08), 0 0 0 0.5px rgba(189,178,255,0.12)` : "0 4px 24px rgba(189,178,255,0.03)", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg,transparent,${accent},transparent)`, opacity: hov ? 0.9 : 0.35, transition: "opacity 0.18s" }} />
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 18 }}>
        <span style={{ fontSize: 17 }}>{icon}</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: "#dde5ff", flex: 1, fontFamily: "'Manrope',sans-serif" }}>{title}</span>
        <span style={{ fontSize: 14, color: hov ? accent : "#40485c", transition: "color 0.18s" }}>→</span>
      </div>
      {loading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}><Skeleton w="80%" h={14} /><Skeleton w="60%" h={12} /><Skeleton w="90%" h={12} /><Skeleton w="70%" h={12} /></div>
      ) : error ? (
        <div style={{ fontSize: 12, color: "#ff6b6b" }}>Could not load — {error}</div>
      ) : children}
    </div>
  );
}

const Dashboard: React.FC = () => {
  const [trainingOpen, setTrainingOpen] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const ago30 = new Date(Date.now() - 30 * 864e5).toISOString().slice(0, 10);
  const dateLabel = new Date().toLocaleDateString("en-AE", { weekday: "long", day: "numeric", month: "long" });

  const waStats  = useGet<any>("/whatsapp/stats");
  const waConvos = useGet<any>("/whatsapp/conversations");
  const pipeline = useGet<any>("/crm/pipeline/summary");
  const crmLeads = useGet<any>("/crm/leads?limit=5");
  const empKpi   = useGet<any>("/employee/kpi/per-employee?days=30");
  const empLogs  = useGet<any>("/employee/logs?days=1");
  const invKpis  = useGet<any>("/inventory/kpis");
  const invLow   = useGet<any>("/inventory/low-stock");
  const profit   = useGet<any>(`/shopify/profit?from=${ago30}&to=${today}`);

  const aed = (n: any) => n != null ? `AED ${Number(n).toLocaleString("en", { maximumFractionDigits: 0 })}` : "—";

  // Real API shapes:
  // pipeline -> { by_status: { new, contacted, qualified, proposal, won, lost, total } }
  // empKpi   -> { employees: [...], period_days }
  // invLow   -> { items: [], total }
  const pipelineData   = pipeline.data?.by_status ?? pipeline.data?.pipeline ?? pipeline.data;
  const pipelineStages = [
    { key: "new",       label: "New",       color: "#c8bfff" },
    { key: "contacted", label: "Contacted", color: "#ffd6a5" },
    { key: "qualified", label: "Qualified", color: "#25D366" },
    { key: "proposal",  label: "Proposal",  color: "#60a5fa" },
    { key: "won",       label: "Won",       color: "#b8d58d" },
    { key: "lost",      label: "Lost",      color: "#ff6b6b" },
  ];
  const pipelineTotal = pipelineStages.reduce((s, st) => s + (pipelineData?.[st.key] || 0), 0);

  const empList = Array.isArray(empKpi.data) ? empKpi.data : (empKpi.data?.employees ?? []);
  const avgAttendance = empList.length
    ? Math.round(empList.reduce((s: number, e: any) => s + (e.attendance_rate || 0), 0) / empList.length)
    : null;

  const lowStockItems: any[] = invLow.data?.items ?? (Array.isArray(invLow.data) ? invLow.data : []);
  const lateToday = (empLogs.data?.logs ?? []).filter((l: any) => l.tactical_delay);
  const leads: any[] = crmLeads.data?.leads ?? crmLeads.data?.items ?? (Array.isArray(crmLeads.data) ? crmLeads.data : []);
  const convos: any[] = waConvos.data?.conversations ?? (Array.isArray(waConvos.data) ? waConvos.data : []);

  return (
    <div style={{ padding: "32px 0", fontFamily: "'Space Grotesk', sans-serif" }}>
      <style>{`@keyframes pulse{0%,100%{opacity:.5}50%{opacity:1}}`}</style>

      {/* header */}
      <div style={{ display: "flex", alignItems: "baseline", gap: 14, marginBottom: 32 }}>
        <h1 style={{ margin: 0, fontSize: 30, fontWeight: 800, color: "#dde5ff", fontFamily: "'Manrope', sans-serif", letterSpacing: "-0.02em" }}>Operations Center</h1>
        <span style={{ fontSize: 11, color: "#6d758b", letterSpacing: "0.04em", fontFamily: "'Space Grotesk',sans-serif" }}>Live · {dateLabel}</span>
        <button onClick={() => setTrainingOpen(true)} title="Open training guide" style={{ marginLeft: "auto", background: "rgba(255,214,165,0.1)", border: "1px solid rgba(255,214,165,0.2)", borderRadius: 8, color: "#ffd6a5", cursor: "pointer", fontSize: 14, fontWeight: 700, padding: "3px 10px" }}>?</button>
      </div>
      <TrainingGuide moduleKey="dashboard" steps={dashboardSteps} isOpen={trainingOpen} onClose={() => setTrainingOpen(false)} />

      {/* KPI strip */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 12, marginBottom: 24 }}>
        <KPIBox label="Net Revenue (30d)"
          value={profit.data?.summary?.net_sales != null ? aed(profit.data.summary.net_sales) : "—"}
          sub={profit.data?.summary?.total_profit != null ? `Profit: ${aed(profit.data.summary.total_profit)}` : undefined}
          color="#b8d58d" loading={profit.loading} />
        <KPIBox label="WhatsApp Convos"
          value={waStats.data?.total_conversations ?? "—"}
          sub={`${waStats.data?.pending_leads ?? 0} pending leads`}
          color="#25D366" loading={waStats.loading} />
        <KPIBox label="CRM Pipeline"
          value={pipelineTotal > 0 ? pipelineTotal : "—"}
          sub={`${pipelineData?.new ?? 0} new · ${pipelineData?.won ?? 0} won`}
          color="#ffd6a5" loading={pipeline.loading} />
        <KPIBox label="Avg Attendance"
          value={avgAttendance != null ? `${avgAttendance}%` : "—"}
          sub="team · last 30 days"
          color="#c8bfff" loading={empKpi.loading} />
        <KPIBox label="Low Stock Alerts"
          value={lowStockItems.length > 0 ? lowStockItems.length : "✓ OK"}
          sub={lowStockItems.length > 0 ? "items need reorder" : "all levels healthy"}
          color={lowStockItems.length > 0 ? "#ffb4ab" : "#b8d58d"} loading={invLow.loading} />
      </div>

      {/* Row 1: WhatsApp + CRM */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>

        <ModuleCard title="WhatsApp Intelligence" icon="💬" href="/whatsapp" accent="#25D366"
          loading={waConvos.loading && waStats.loading}
          error={waConvos.error && waStats.error ? waStats.error : null}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 16 }}>
            {([
              { label: "Conversations", v: waStats.data?.total_conversations ?? "—", c: "#25D366" },
              { label: "Leads Created", v: waStats.data?.leads_created ?? "—",       c: "#b8d58d" },
              { label: "Pending",       v: waStats.data?.pending_leads ?? "—",       c: "#ffd6a5" },
            ] as { label: string; v: any; c: string }[]).map(s => (
              <div key={s.label} style={{ textAlign: "center", padding: "10px 6px", background: "rgba(255,255,255,0.03)", borderRadius: 10 }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: s.c, fontFamily: "'Manrope', sans-serif" }}>{s.v}</div>
                <div style={{ fontSize: 10, color: "#6b7280", marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>
          <SectionTitle>Recent Conversations</SectionTitle>
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            {convos.slice(0, 5).map((c: any, i: number) => (
              <Row key={i}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", flexShrink: 0, background: c.in_live_agent_flow ? "#ffd6a5" : "#25D366" }} />
                <span style={{ color: "#dae2fd", flex: 1, fontFamily: "monospace", fontSize: 11 }}>+{(c.phone || "").replace(/@c\.us|@g\.us/g, "")}</span>
                <span style={{ color: "#6b7280", fontSize: 11 }}>{c.message_count}m</span>
                {c.in_live_agent_flow && <Tag color="#ffd6a5">live</Tag>}
                {c.lead_created      && <Tag color="#b8d58d">lead</Tag>}
                {c.awaiting_order    && <Tag color="#c8bfff">order</Tag>}
              </Row>
            ))}
            {convos.length === 0 && !waConvos.loading && <div style={{ color: "#4a5568", fontSize: 12, padding: "8px 0" }}>No conversations yet</div>}
          </div>
        </ModuleCard>

        <ModuleCard title="CRM Pipeline" icon="👥" href="/crm" accent="#ffd6a5"
          loading={pipeline.loading} error={pipeline.error}>
          {pipelineData && (
            <>
              <div style={{ display: "flex", height: 9, borderRadius: 5, overflow: "hidden", gap: 1.5, marginBottom: 12 }}>
                {pipelineStages.map(st => {
                  const pct = pipelineTotal > 0 ? ((pipelineData[st.key] || 0) / pipelineTotal) * 100 : 0;
                  return pct > 0 ? <div key={st.key} style={{ width: `${pct}%`, background: st.color, opacity: 0.85 }} /> : null;
                })}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 6, marginBottom: 16 }}>
                {pipelineStages.map(st => (
                  <div key={st.key} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <span style={{ width: 7, height: 7, borderRadius: 2, background: st.color, flexShrink: 0 }} />
                    <span style={{ fontSize: 11, color: "#6b7280", flex: 1 }}>{st.label}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#dae2fd" }}>{pipelineData[st.key] || 0}</span>
                  </div>
                ))}
              </div>
            </>
          )}
          <SectionTitle>Recent Leads</SectionTitle>
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            {leads.slice(0, 5).map((lead: any, i: number) => (
              <Row key={i}>
                <span style={{ color: "#dae2fd", flex: 1, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{lead.name}</span>
                {lead.country && <span style={{ color: "#6b7280", fontSize: 11 }}>{lead.country}</span>}
                <Tag color={lead.status === "won" ? "#b8d58d" : lead.status === "lost" ? "#ff6b6b" : lead.status === "new" ? "#c8bfff" : lead.status === "proposal" ? "#60a5fa" : "#ffd6a5"}>
                  {lead.status}
                </Tag>
              </Row>
            ))}
            {leads.length === 0 && !crmLeads.loading && <div style={{ color: "#4a5568", fontSize: 12, padding: "8px 0" }}>No leads yet</div>}
          </div>
        </ModuleCard>
      </div>

      {/* Row 2: Attendance + Inventory + Profit */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>

        <ModuleCard title="Attendance & KPI" icon="🕐" href="/attendance" accent="#c8bfff"
          loading={empKpi.loading} error={empKpi.error}>
          {lateToday.length > 0 && (
            <div style={{ padding: "7px 12px", background: "rgba(255,180,171,0.08)", border: "1px solid rgba(255,107,107,0.2)", borderRadius: 9, fontSize: 12, color: "#ffb4ab", marginBottom: 14 }}>
              ⚠ {lateToday.length} late arrival{lateToday.length > 1 ? "s" : ""} today
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {empList.map((emp: any, i: number) => {
              const rate  = Math.round(emp.attendance_rate || 0);
              const color = rate >= 70 ? "#b8d58d" : rate >= 50 ? "#ffd6a5" : "#ff6b6b";
              return (
                <div key={i}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                    <span style={{ fontSize: 12, color: "#dae2fd", fontWeight: 500 }}>{emp.employee_name}</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      {emp.late_count > 0 && <span style={{ fontSize: 10, color: "#ffd6a5" }}>{emp.late_count} late</span>}
                      <span style={{ fontSize: 13, fontWeight: 700, color }}>{rate}%</span>
                    </div>
                  </div>
                  <Bar pct={rate} color={color} />
                </div>
              );
            })}
            {empList.length === 0 && <div style={{ color: "#4a5568", fontSize: 12 }}>No employee data</div>}
          </div>
        </ModuleCard>

        <ModuleCard title="Inventory" icon="📦" href="/inventory" accent="#b8d58d"
          loading={invKpis.loading} error={invKpis.error}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
            <div style={{ padding: "10px 12px", background: "rgba(255,255,255,0.03)", borderRadius: 10 }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: "#b8d58d", fontFamily: "'Manrope', sans-serif" }}>
                {invKpis.data?.total_products ?? invKpis.data?.total_skus ?? "—"}
              </div>
              <div style={{ fontSize: 10, color: "#6b7280", marginTop: 2 }}>Total SKUs</div>
            </div>
            <div style={{ padding: "10px 12px", background: "rgba(255,255,255,0.03)", borderRadius: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#ffd6a5", fontFamily: "'Manrope', sans-serif" }}>
                {invKpis.data?.total_inventory_value != null ? aed(invKpis.data.total_inventory_value) : "—"}
              </div>
              <div style={{ fontSize: 10, color: "#6b7280", marginTop: 2 }}>Stock Value</div>
            </div>
          </div>
          <SectionTitle>{lowStockItems.length > 0 ? `Low Stock (${lowStockItems.length})` : "Stock Levels"}</SectionTitle>
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            {lowStockItems.slice(0, 5).map((item: any, i: number) => (
              <Row key={i}>
                <span style={{ color: "#dae2fd", flex: 1, fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {item.title ?? item.name ?? item.product_name ?? "Product"}
                </span>
                <span style={{ color: (item.quantity ?? item.inventory_quantity ?? 0) <= 2 ? "#ff6b6b" : "#ffb4ab", fontWeight: 700, fontSize: 12 }}>
                  {item.quantity ?? item.inventory_quantity ?? 0} left
                </span>
              </Row>
            ))}
            {lowStockItems.length === 0 && <div style={{ fontSize: 12, color: "#b8d58d", padding: "4px 0" }}>✓ All stock levels OK</div>}
          </div>
        </ModuleCard>

        <ModuleCard title="Shopify Profit" icon="💰" href="/profit" accent="#ffd6a5"
          loading={profit.loading} error={profit.error}>
          {profit.data?.summary && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
                {([
                  { label: "Orders",    value: profit.data.summary.total_orders,     color: "#c8bfff" },
                  { label: "Gross",     value: aed(profit.data.summary.gross_sales),  color: "#dae2fd" },
                  { label: "Net Sales", value: aed(profit.data.summary.net_sales),    color: "#ffd6a5" },
                  { label: "Profit",    value: aed(profit.data.summary.total_profit), color: profit.data.summary.total_profit > 0 ? "#b8d58d" : "#ff6b6b" },
                ] as { label: string; value: any; color: string }[]).map(s => (
                  <div key={s.label} style={{ padding: "10px 12px", background: "rgba(255,255,255,0.03)", borderRadius: 10 }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: s.color, fontFamily: "'Manrope', sans-serif", lineHeight: 1.1 }}>{s.value ?? "—"}</div>
                    <div style={{ fontSize: 10, color: "#6b7280", marginTop: 2 }}>{s.label}</div>
                  </div>
                ))}
              </div>
              <SectionTitle>By Channel</SectionTitle>
              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                {([
                  { icon: "🌐", label: "Online", d: profit.data.online },
                  { icon: "🏪", label: "POS",    d: profit.data.pos },
                ] as { icon: string; label: string; d: any }[]).filter(c => c.d?.orders > 0).map(ch => (
                  <Row key={ch.label}>
                    <span>{ch.icon}</span>
                    <span style={{ color: "#dae2fd", flex: 1 }}>{ch.label}</span>
                    <span style={{ color: "#6b7280", fontSize: 11 }}>{ch.d.orders} orders</span>
                    <span style={{ color: "#b8d58d", fontWeight: 700 }}>{aed(ch.d.sold ?? ch.d.net_sales)}</span>
                  </Row>
                ))}
              </div>
              {profit.data.summary.discounts > 0 && (
                <div style={{ marginTop: 10, fontSize: 11, color: "#6b7280" }}>Discounts: {aed(profit.data.summary.discounts)}</div>
              )}
            </>
          )}
        </ModuleCard>
      </div>
    </div>
  );
};

export default Dashboard;
